import React, { forwardRef, useRef } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';

import { Box, Flex, SvgIcon, Text } from 'Components/Base';

const ArrowButton = styled((props) => (
  <Flex
    width={'50px'}
    height={'49px'}
    alignItems={'center'}
    justifyContent={'center'}
    bg={'white'}
    {...props}
  />
))`
  position: absolute;

  ${({ isLeft }) =>
    isLeft
      ? css`
          left: 0;
          box-shadow: 2px 0px 4px rgba(0, 0, 0, 0.1);
        `
      : css`
          right: 0;
          box-shadow: -2px 0px 4px rgba(0, 0, 0, 0.1);
        `};
  top: 0;
  cursor: pointer;
  z-index: 1;
`;

const ItemListContainer = styled((props) => <Flex width={'100%'} minHeight={'50px'} {...props} />)`
  position: relative;
  box-shadow: inset 0 -1px 0 0 #e5e5e5;
  overflow: hidden;
  * {
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

const ItemItem = styled((props) => (
  <Flex height={'100%'} px={4} justifyContent={'center'} alignItems={'center'} {...props} />
))`
  cursor: pointer;
  border-bottom: 2px solid transparent;
  ${({ active }) =>
    active &&
    css`
      border-color: ${({ theme }) => theme.colors.primary};
    `}
`;

const ItemWithRef = forwardRef((props, ref) => (
  <Box ref={ref} style={{ flexShrink: 0 }}>
    <ItemItem {...props} />
  </Box>
));

ItemWithRef.displayName = 'ItemWithRef';

export const SCROLL_STATE = {
  START: 'START',
  END: 'END',
  MIDDLE: 'MIDDLE',
};

const TabList = ({
  currentItemKey,
  setCurrentItemKey,
  currentItem,
  scrollState,
  setScrollState,
  itemKeys,
  items,
  showArrow,
}) => {
  const itemRef = useRef([]);
  const itemListRef = useRef();

  const scrollX = (offset) => {
    itemListRef.current.scrollLeft += offset;
  };

  const scrollItemIntoView = (index) => {
    itemRef.current[index].scrollIntoView({ block: 'nearest', inline: 'start' });
  };

  const handleScroll = () => {
    const maxOffset = itemListRef.current.scrollWidth - itemListRef.current.offsetWidth;
    const offset = itemListRef.current.scrollLeft;

    if (offset <= 50) return setScrollState(SCROLL_STATE.START);
    if (offset + 50 >= maxOffset) return setScrollState(SCROLL_STATE.END);
    setScrollState(SCROLL_STATE.MIDDLE);
  };

  return (
    <>
      <ItemListContainer>
        {showArrow && scrollState !== SCROLL_STATE.START && (
          <ArrowButton onClick={() => scrollX(-120)} isLeft>
            <SvgIcon icon={SvgIcon.ICON_LIST.BACK} size={24} color="text" />
          </ArrowButton>
        )}
        {showArrow && scrollState !== SCROLL_STATE.END && (
          <ArrowButton onClick={() => scrollX(120)}>
            <SvgIcon icon={SvgIcon.ICON_LIST.ARROW_RIGHT} size={24} color="text" />
          </ArrowButton>
        )}
        <Flex
          onScroll={handleScroll}
          ref={itemListRef}
          ml={showArrow && scrollState !== SCROLL_STATE.START ? '50px' : 0}
          mr={showArrow && scrollState !== SCROLL_STATE.END ? '50px' : 0}
          style={{ overflowX: 'scroll' }}
        >
          {itemKeys.map((itemKey, index) => (
            <ItemWithRef
              ref={(ref) => (itemRef.current[index] = ref)}
              key={itemKey}
              onClick={() => {
                scrollItemIntoView(index);
                setCurrentItemKey(itemKey);
              }}
              active={currentItemKey === itemKey}
            >
              <Text color={currentItemKey === itemKey ? 'primary' : 'text'}>{items[itemKey]}</Text>
            </ItemWithRef>
          ))}
        </Flex>
      </ItemListContainer>
      {currentItem}
    </>
  );
};

TabList.propTypes = {
  currentItemKey: PropTypes.string,
  setCurrentItemKey: PropTypes.func,
  currentItem: PropTypes.node,
  scrollState: PropTypes.oneOf(['START', 'MIDDLE', 'END']),
  setScrollState: PropTypes.func,
  itemKeys: PropTypes.arrayOf(PropTypes.string),
  items: PropTypes.objectOf(PropTypes.string),
  showArrow: PropTypes.bool,
};

export default TabList;
